import type { History, View } from './types';
import { BS_TAB, VIEW_TO_TAB } from './types';
import { loadCashFlowSchema } from './cashFlowSchema';
import { loadSchema, parseNumeric, type TabData } from './schema';
import { consolidateHistoryMonth } from './consolidate';

export interface CgmFixture {
    control: {
        last_closed_month: string;
    };
    tabs: Record<string, TabData>;
}

export function historyFromFixture(fixture: CgmFixture): History {
    const schema = loadSchema();
    const values: History['values'] = {
        river_oaks: {},
        highland_park: {},
        ga: {},
        consolidated: {},
    };

    let nHistory = 0;
    const refTab = fixture.tabs[VIEW_TO_TAB.river_oaks];
    const statusRow = refTab?.status ?? [];
    const statuses = (statusRow[0] ?? []) as unknown[];
    for (let i = 7; i < statuses.length; i += 1) {
        if (String(statuses[i] ?? '').trim().toLowerCase() === 'actual') {
            nHistory += 1;
        }
    }

    for (const view of ['river_oaks', 'highland_park', 'ga'] as const) {
        const tab = fixture.tabs[VIEW_TO_TAB[view]];
        if (tab) {
            fillHistoryFromTab(values[view], tab, schema, nHistory, 'IS');
        }
    }

    const bsTab = fixture.tabs[BS_TAB];
    if (bsTab) {
        fillHistoryFromTab(values.consolidated, bsTab, schema, nHistory, 'BS');
    }

    const cfTab = fixture.tabs.CF_FULL;
    if (cfTab) {
        fillCfHistoryFromTab(values.consolidated, cfTab, nHistory);
    }

    const emptyInputsForHistory = { consolidated_only: [], drivers: {}, one_time_items: [], cash_policy: null };
    for (let histIdx = 0; histIdx < nHistory; histIdx += 1) {
        consolidateHistoryMonth(values, schema, emptyInputsForHistory, histIdx, nHistory);
    }

    return {
        nHistory,
        lastClosedMonth: fixture.control.last_closed_month,
        values,
    };
}

function fillHistoryFromTab(
    target: Record<string, (number | string | null)[]>,
    tab: TabData,
    schema: ReturnType<typeof loadSchema>,
    nHistory: number,
    tabKind: 'IS' | 'BS',
): void {
    const values = tab.values ?? [];
    for (const entry of schema) {
        if (entry.tab !== tabKind || entry.row == null) {
            continue;
        }
        const offset = entry.row - 21;
        if (offset < 0 || offset >= values.length) {
            continue;
        }
        const rowValues = (values[offset] ?? []) as unknown[];
        const isText = entry.kind === 'text';
        const series: (number | string | null)[] = [];
        for (let monthIdx = 0; monthIdx < nHistory; monthIdx += 1) {
            const raw = monthIdx < rowValues.length ? rowValues[monthIdx] : null;
            series.push(parseNumeric(raw, { textMode: isText }));
        }
        target[entry.key] = series;
    }
}

function fillCfHistoryFromTab(
    target: Record<string, (number | string | null)[]>,
    tab: TabData,
    nHistory: number,
): void {
    const values = tab.values ?? [];
    for (const entry of loadCashFlowSchema()) {
        if (entry.row == null) {
            continue;
        }
        const offset = entry.row - 21;
        if (offset < 0 || offset >= values.length) {
            continue;
        }
        const rowValues = (values[offset] ?? []) as unknown[];
        const isText = entry.unit === 'text';
        const series: (number | string | null)[] = [];
        for (let monthIdx = 0; monthIdx < nHistory; monthIdx += 1) {
            const raw = monthIdx < rowValues.length ? rowValues[monthIdx] : null;
            series.push(parseNumeric(raw, { textMode: isText }));
        }
        target[entry.key] = series;
    }
}

export function setDriver(
    inputs: import('./types').CaseInputs,
    view: View,
    line: string,
    type: import('./types').DriverType,
    driverValues: (number | null)[],
    params?: import('./types').DriverParams,
): void {
    if (!inputs.drivers[view]) {
        inputs.drivers[view] = {};
    }
    inputs.drivers[view]![line] = {
        active: type,
        by_type: {
            [type]: {
                values: driverValues,
                params,
            },
        },
    };
}
